import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Button } from '@reflex/ui/components/ui/button';
import { MarkdownContent } from '@reflex/ui/components/content/MarkdownContent';
import { SHIMMER_TEXT_CLASS } from '@reflex/ui/components/content/WorkingIndicator';
import { EventDetailTooltip } from '@reflex/ui/components/stream/EventMeta';
import type { ThinkingBlock } from '../types.js';
import { rendersNothing } from './block-visibility.js';
import { thinkingHeightTransition } from './thinking-transition.js';

const ACTIVE_MAX_HEIGHT = 128;

export function ThinkingBlockView({ block }: { block: ThinkingBlock }) {
  const [showFull, setShowFull] = useState(false);
  const reduceMotion = useReducedMotion();
  const activeRef = useRef<HTMLDivElement>(null);
  const hasText = !!block.text;

  const isMultiLine = hasText && (block.text!.includes('\n') || block.text!.length > 120);

  useEffect(() => {
    if (block.isActive && !showFull && activeRef.current) {
      activeRef.current.scrollTop = activeRef.current.scrollHeight;
    }
  }, [block.text, block.isActive, showFull]);

  if (rendersNothing(block)) return null;

  const firstLine = (block.text ?? '').split('\n').find((l) => l.trim()) ?? '';

  return (
    <div className="group/thinking relative -ml-[21px] flex items-start gap-2 rounded-lg px-2.5 py-0.5">
      <div className="h-1.5 w-1.5 shrink-0" aria-hidden />
      <div className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {block.sourceEvent && (
        <span className="pointer-events-none absolute right-0 top-0 opacity-0 transition-opacity group-hover/thinking:pointer-events-auto group-hover/thinking:opacity-100">
          <EventDetailTooltip event={block.sourceEvent} />
        </span>
      )}
      {/* The content column animates `height: auto` so its height changes don't
          snap. The jarring one is the active → settled collapse: reasoning
          fills a ≤128px box while thinking, then settles to a single truncated
          line — an instant ~100px drop that shoves everything below the turn
          up in one frame. Animating it smooths that collapse and the
          expand/collapse toggle of the settled text. The transition is off
          while the block is still active (see thinking-transition) so
          streaming reasoning tracks its growing box crisply instead of lagging
          behind its own height transition, and when motion is reduced. */}
      <motion.div
        className="min-w-0 flex-1"
        initial={false}
        animate={{ height: 'auto' }}
        transition={thinkingHeightTransition(block.isActive, reduceMotion)}
        style={{ overflow: 'hidden' }}
      >
        {block.isActive ? (
          <>
            <span className={`text-xs ${SHIMMER_TEXT_CLASS}`}>Thinking&hellip;</span>
            {hasText && (
              <div
                ref={activeRef}
                className="mt-0.5 text-xs text-foreground/50 leading-relaxed overflow-hidden"
                style={{ maxHeight: ACTIVE_MAX_HEIGHT }}
              >
                <MarkdownContent
                  content={block.text ?? ''}
                  size="xs"
                  className="thinking-markdown [&_p]:text-foreground/50"
                />
              </div>
            )}
          </>
        ) : showFull ? (
          // Expanded: markdown can contain its own interactive elements (copy buttons,
          // links, focusable <pre>) so we can't make the outer container a button.
          // Instead, a dedicated collapse toggle sits above the rendered markdown.
          <div className="text-xs text-foreground/40 italic leading-relaxed">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setShowFull(false)}
              aria-label="Collapse thinking"
              aria-expanded="true"
              className="mb-1 h-auto p-0 text-3xs font-normal uppercase tracking-wider text-foreground/40 hover:bg-transparent hover:text-foreground/60 dark:hover:bg-transparent"
            >
              Collapse
            </Button>
            <MarkdownContent
              content={block.text ?? ''}
              size="xs"
              className="thinking-markdown [&_p]:text-foreground/40 [&_p]:italic [&_code]:not-italic [&_pre]:not-italic [&_p]:mb-1.5 [&_ul]:mb-1.5 [&_ol]:mb-1.5"
            />
          </div>
        ) : isMultiLine ? (
          <button
            type="button"
            onClick={() => setShowFull(true)}
            aria-label="Expand thinking"
            aria-expanded="false"
            className="block w-full truncate text-left text-xs text-foreground/40 italic leading-relaxed cursor-pointer hover:text-foreground/60 transition-colors"
          >
            {firstLine}
            <span className="text-foreground/30">&nbsp;&hellip;</span>
          </button>
        ) : (
          <div className="truncate text-xs text-foreground/40 italic leading-relaxed">
            {firstLine}
          </div>
        )}
      </motion.div>
    </div>
  );
}
